import React from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import type { MetaBidStrategy } from '@/services/api/metaAds';
import type { MetaBudgetMode } from './MetaBudgetStep';
import { CONSTRAINED_BID_STRATEGIES } from '../constants/metaAdSetMatrix';

interface AdSetBudgetStepProps {
  /** Parent campaign's bid_strategy — gates whether the bid_amount field
   *  is rendered. CONSTRAINED_BID_STRATEGIES = {BID_CAP, COST_CAP, MIN_ROAS}.
   *  Backend re-validates after parent-campaign lookup. */
  parentBidStrategy: string | null;
  /** When true the parent campaign carries the budget (CBO mode) and the
   *  ad-set budget controls are hidden. The step still renders when CBO
   *  pairs with a constrained bid_strategy so the user can supply the
   *  required `bid_amount`. */
  parentIsCbo: boolean;
  budgetMode: MetaBudgetMode;
  budgetAmount: string;
  bidAmount: string;
  onChangeMode: (mode: MetaBudgetMode) => void;
  onChangeAmount: (amount: string) => void;
  onChangeBidAmount: (bidAmount: string) => void;
  errors?: {
    amount?: string;
    bidAmount?: string;
  };
}

/**
 * Step 2 — budget mode (daily XOR lifetime) + amount + conditional
 * bid_amount.
 *
 * Bid amount only appears when the parent campaign's bid_strategy is one
 * of the constrained strategies (BID_CAP / COST_CAP / MIN_ROAS) — Meta
 * requires a cap / floor value in those cases. For LOWEST_COST_WITHOUT_CAP
 * (the default campaign-level strategy), bid_amount is omitted entirely.
 */
export const AdSetBudgetStep: React.FC<AdSetBudgetStepProps> = ({
  parentBidStrategy,
  parentIsCbo,
  budgetMode,
  budgetAmount,
  bidAmount,
  onChangeMode,
  onChangeAmount,
  onChangeBidAmount,
  errors,
}) => {
  const showBidAmount =
    parentBidStrategy !== null &&
    // The Campaign type surfaces bid_strategy as `string`; the set is
    // typed to `MetaBidStrategy` so the body still catches typos. The
    // narrowing cast at the lookup is structurally safe (Set.has is a
    // string lookup at runtime).
    CONSTRAINED_BID_STRATEGIES.has(parentBidStrategy as MetaBidStrategy);
  // MIN_ROAS uses a decimal multiplier (e.g. 1.5 for 1.5× ROAS), not
  // money — the bid amount field formatting differs accordingly.
  const isMinRoas = parentBidStrategy === 'LOWEST_COST_WITH_MIN_ROAS';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {parentIsCbo ? 'Bid' : 'Budget & bid'}
        </h3>
        <p className="text-sm text-gray-600">
          {parentIsCbo
            ? 'The parent campaign carries the budget (Campaign Budget Optimization). Set the per-ad-set bid for the constrained bid strategy below.'
            : 'Set the spend ceiling for this ad set. Lifetime budgets need a stop date on the next step.'}
        </p>
      </div>

      {!parentIsCbo && (
        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">
            Budget mode
          </p>
          <div role="radiogroup" aria-label="Budget mode" className="flex gap-2">
            {(['daily', 'lifetime'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={budgetMode === mode}
                onClick={() => onChangeMode(mode)}
                className={cn(
                  'flex-1 p-3 border-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                  budgetMode === mode
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-700',
                )}
              >
                {mode === 'daily' ? 'Daily budget' : 'Lifetime budget'}
              </button>
            ))}
          </div>
        </div>
      )}

      {!parentIsCbo && (
      <div>
        <label
          htmlFor="meta-ad-set-budget-amount"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Amount (USD)
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            $
          </span>
          <Input
            id="meta-ad-set-budget-amount"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={budgetAmount}
            onChange={(e) => onChangeAmount(e.target.value)}
            placeholder={budgetMode === 'daily' ? '25.00' : '500.00'}
            className="pl-7"
            aria-invalid={Boolean(errors?.amount)}
            aria-describedby={
              errors?.amount
                ? 'meta-ad-set-budget-amount-error'
                : 'meta-ad-set-budget-amount-hint'
            }
          />
        </div>
        <p
          id="meta-ad-set-budget-amount-hint"
          className="text-xs text-gray-500 mt-1"
        >
          Meta enforces per-objective minimums. If Meta rejects the ad set
          for being below the floor, the message will tell you the minimum.
        </p>
        {errors?.amount && (
          <p
            id="meta-ad-set-budget-amount-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.amount}
          </p>
        )}
      </div>
      )}

      {showBidAmount && (
        <div>
          <label
            htmlFor="meta-ad-set-bid-amount"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {isMinRoas ? 'Minimum ROAS goal' : 'Bid amount (USD)'}{' '}
            <span className="text-red-500" aria-label="required">*</span>
          </label>
          <div className="relative">
            {!isMinRoas && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
            )}
            <Input
              id="meta-ad-set-bid-amount"
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              value={bidAmount}
              onChange={(e) => onChangeBidAmount(e.target.value)}
              placeholder={isMinRoas ? '1.5' : '1.50'}
              className={isMinRoas ? '' : 'pl-7'}
              aria-invalid={Boolean(errors?.bidAmount)}
              aria-describedby={
                errors?.bidAmount
                  ? 'meta-ad-set-bid-amount-error'
                  : 'meta-ad-set-bid-amount-hint'
              }
              required
            />
          </div>
          <p
            id="meta-ad-set-bid-amount-hint"
            className="text-xs text-gray-500 mt-1"
          >
            {isMinRoas ? (
              <>
                Required because the parent campaign uses{' '}
                {parentBidStrategy}. Enter the minimum ROAS goal as a
                decimal multiplier (e.g. <code>1.5</code> means a 1.5×
                return on ad spend). Backend converts to Meta's wire unit.
              </>
            ) : (
              <>
                Required because the parent campaign uses{' '}
                {parentBidStrategy}. Enter the per-result cap in USD
                (e.g. <code>1.50</code> = $1.50 cap per
                {parentBidStrategy === 'COST_CAP' ? ' result' : ' bid'}).
              </>
            )}
          </p>
          {errors?.bidAmount && (
            <p
              id="meta-ad-set-bid-amount-error"
              role="alert"
              className="text-sm text-red-600 mt-1"
            >
              {errors.bidAmount}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
